// NOTE: The diagram contract has two halves that must not be confused.
//
// 1. The SPEC (`TDiagramSpec`) is what the assistant writes: meaning only —
//    nodes, edges, lanes, events. It carries no coordinates. A model asked to
//    place geometry by hand gets one shot and produces overlapping boxes and
//    arrows that miss their targets, so geometry is never its job.
// 2. The SCENE (`IDiagramScene`) is what the layout engine computes: resolved
//    geometry in a flat, paint-ordered list of primitives that web, mobile and
//    the server render identically.
//
// Colours in the scene are semantic ROLES, never literal hex values, so the
// same scene renders in the default skin or in an organisation's brand
// (NSAI-5121) without relayout.

export enum EDiagramType {
  ARCHITECTURE = "architecture",
  FLOWCHART = "flowchart",
  SEQUENCE = "sequence",
  SWIMLANE = "swimlane",
  TIMELINE = "timeline",
  ORG_CHART = "orgChart",
  LAYERS = "layers",
  QUADRANT = "quadrant",
  MATRIX = "matrix",
}

// NOTE: Semantic colour roles. `accent` is the single focal colour and is
// reserved for the one or two nodes that carry the point of the figure.
export enum EDiagramColorRole {
  PAPER = "paper",
  PAPER_2 = "paper2",
  INK = "ink",
  MUTED = "muted",
  SOFT = "soft",
  RULE = "rule",
  RULE_SOLID = "ruleSolid",
  ACCENT = "accent",
  ACCENT_TINT = "accentTint",
  LINK = "link",
}

export enum EDiagramFontFamily {
  SANS = "sans",
  SERIF = "serif",
  MONO = "mono",
}

export enum EDiagramTextAnchor {
  START = "start",
  MIDDLE = "middle",
  END = "end",
}

export type TDiagramTokens = Record<EDiagramColorRole, string>;

export interface IDiagramFontStacks {
  sans: string;
  serif: string;
  mono: string;
}

export interface IDiagramTheme {
  tokens: TDiagramTokens;
  fonts: IDiagramFontStacks;
}

// ---------------------------------------------------------------------------
// Spec — what the assistant writes
// ---------------------------------------------------------------------------

export enum EDiagramNodeKind {
  DEFAULT = "default",
  CLIENT = "client",
  SERVICE = "service",
  STORE = "store",
  QUEUE = "queue",
  EXTERNAL = "external",
}

export enum EDiagramEdgeStyle {
  SOLID = "solid",
  DASHED = "dashed",
}

export enum EDiagramEdgeKind {
  DEFAULT = "default",
  EXTERNAL = "external",
}

export interface IDiagramNode {
  id: string;
  label: string;
  sublabel?: string | undefined;
  group?: string | undefined;
  kind?: EDiagramNodeKind | undefined;
  focal?: boolean | undefined;
}

export interface IDiagramEdge {
  from: string;
  to: string;
  label?: string | undefined;
  style?: EDiagramEdgeStyle | undefined;
  kind?: EDiagramEdgeKind | undefined;
  bidirectional?: boolean | undefined;
}

export interface IDiagramSpecBase {
  title?: string | undefined;
  subtitle?: string | undefined;
}

export interface IArchitectureDiagramSpec extends IDiagramSpecBase {
  type: EDiagramType.ARCHITECTURE;
  nodes: IDiagramNode[];
  edges: IDiagramEdge[];
}

export enum EFlowchartStepKind {
  START = "start",
  PROCESS = "process",
  DECISION = "decision",
  END = "end",
}

export interface IFlowchartStep {
  id: string;
  label: string;
  kind: EFlowchartStepKind;
  focal?: boolean | undefined;
}

export interface IFlowchartDiagramSpec extends IDiagramSpecBase {
  type: EDiagramType.FLOWCHART;
  steps: IFlowchartStep[];
  edges: IDiagramEdge[];
}

export interface ISequenceActor {
  id: string;
  label: string;
  focal?: boolean | undefined;
}

export interface ISequenceMessage {
  from: string;
  to: string;
  label: string;
  style?: EDiagramEdgeStyle | undefined;
  note?: string | undefined;
}

export interface ISequenceDiagramSpec extends IDiagramSpecBase {
  type: EDiagramType.SEQUENCE;
  actors: ISequenceActor[];
  messages: ISequenceMessage[];
}

export interface ISwimlaneStep {
  id: string;
  label: string;
  lane: string;
  sublabel?: string | undefined;
  focal?: boolean | undefined;
}

export interface ISwimlaneDiagramSpec extends IDiagramSpecBase {
  type: EDiagramType.SWIMLANE;
  lanes: string[];
  steps: ISwimlaneStep[];
  edges: IDiagramEdge[];
}

export interface ITimelineEvent {
  id: string;
  label: string;
  at: string;
  sublabel?: string | undefined;
  focal?: boolean | undefined;
}

export interface ITimelineDiagramSpec extends IDiagramSpecBase {
  type: EDiagramType.TIMELINE;
  events: ITimelineEvent[];
}

export interface IOrgChartNode {
  id: string;
  label: string;
  parent?: string | undefined;
  sublabel?: string | undefined;
  focal?: boolean | undefined;
}

export interface IOrgChartDiagramSpec extends IDiagramSpecBase {
  type: EDiagramType.ORG_CHART;
  nodes: IOrgChartNode[];
}

export interface IDiagramLayer {
  label: string;
  items?: string[] | undefined;
  sublabel?: string | undefined;
  focal?: boolean | undefined;
}

export interface ILayersDiagramSpec extends IDiagramSpecBase {
  type: EDiagramType.LAYERS;
  layers: IDiagramLayer[];
}

export interface IQuadrantItem {
  id: string;
  label: string;
  x: number;
  y: number;
  focal?: boolean | undefined;
}

export interface IQuadrantAxis {
  label: string;
  low: string;
  high: string;
}

export interface IQuadrantDiagramSpec extends IDiagramSpecBase {
  type: EDiagramType.QUADRANT;
  xAxis: IQuadrantAxis;
  yAxis: IQuadrantAxis;
  items: IQuadrantItem[];
}

// NOTE: NSAI-5125 — a grid of who-may-do-what. The other eight types answer
// "how does this flow"; a matrix answers "which of these combinations hold",
// which is the shape a permission model, a control catalogue or a coverage
// report actually has. A listed cell is a grant; an absent one is not, so the
// safe reading — no access — is the default rather than something a spec has to
// remember to state.
export interface IMatrixAxisEntry {
  id: string;
  label: string;
}

export interface IMatrixCell {
  row: string;
  column: string;
  // NOTE: What kind of grant, when a dot is not specific enough ("read",
  // "RWD"). Absent draws the dot instead, so a cell always carries exactly one
  // visual rather than a glyph and a label fighting for the same space.
  label?: string | undefined;
  focal?: boolean | undefined;
}

export interface IMatrixDiagramSpec extends IDiagramSpecBase {
  type: EDiagramType.MATRIX;
  rows: IMatrixAxisEntry[];
  columns: IMatrixAxisEntry[];
  cells: IMatrixCell[];
}

export type TDiagramSpec =
  | IArchitectureDiagramSpec
  | IFlowchartDiagramSpec
  | ISequenceDiagramSpec
  | ISwimlaneDiagramSpec
  | ITimelineDiagramSpec
  | IOrgChartDiagramSpec
  | ILayersDiagramSpec
  | IQuadrantDiagramSpec
  | IMatrixDiagramSpec;

export interface IParseDiagramSuccess {
  success: true;
  data: TDiagramSpec;
}

export interface IParseDiagramFailure {
  success: false;
  error: string;
}

export type TParseDiagramResult = IParseDiagramSuccess | IParseDiagramFailure;

// ---------------------------------------------------------------------------
// Scene — what the layout engine computes
// ---------------------------------------------------------------------------

export enum ESceneItemKind {
  RECT = "rect",
  LINE = "line",
  PATH = "path",
  POLYGON = "polygon",
  CIRCLE = "circle",
  TEXT = "text",
}

export interface ISceneItemBase {
  id: string;
}

export interface ISceneRect extends ISceneItemBase {
  kind: ESceneItemKind.RECT;
  x: number;
  y: number;
  width: number;
  height: number;
  radius?: number | undefined;
  fill?: EDiagramColorRole | undefined;
  fillOpacity?: number | undefined;
  stroke?: EDiagramColorRole | undefined;
  strokeWidth?: number | undefined;
  dashed?: boolean | undefined;
}

export interface ISceneLine extends ISceneItemBase {
  kind: ESceneItemKind.LINE;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  stroke: EDiagramColorRole;
  strokeWidth?: number | undefined;
  dashed?: boolean | undefined;
}

export interface IScenePath extends ISceneItemBase {
  kind: ESceneItemKind.PATH;
  definition: string;
  stroke?: EDiagramColorRole | undefined;
  strokeWidth?: number | undefined;
  fill?: EDiagramColorRole | undefined;
  fillOpacity?: number | undefined;
  dashed?: boolean | undefined;
}

export interface IScenePoint {
  x: number;
  y: number;
}

export interface IScenePolygon extends ISceneItemBase {
  kind: ESceneItemKind.POLYGON;
  points: IScenePoint[];
  fill?: EDiagramColorRole | undefined;
  fillOpacity?: number | undefined;
  stroke?: EDiagramColorRole | undefined;
  strokeWidth?: number | undefined;
}

export interface ISceneCircle extends ISceneItemBase {
  kind: ESceneItemKind.CIRCLE;
  cx: number;
  cy: number;
  r: number;
  fill?: EDiagramColorRole | undefined;
  stroke?: EDiagramColorRole | undefined;
  strokeWidth?: number | undefined;
}

export interface ISceneText extends ISceneItemBase {
  kind: ESceneItemKind.TEXT;
  x: number;
  y: number;
  lines: string[];
  fontSize: number;
  fill: EDiagramColorRole;
  family?: EDiagramFontFamily | undefined;
  weight?: number | undefined;
  anchor?: EDiagramTextAnchor | undefined;
  letterSpacing?: number | undefined;
  lineHeight?: number | undefined;
}

export type TSceneItem =
  | ISceneRect
  | ISceneLine
  | IScenePath
  | IScenePolygon
  | ISceneCircle
  | ISceneText;

export interface IDiagramScene {
  width: number;
  height: number;
  // NOTE: Paint order. The engines append background bands and group boxes
  // before nodes, and nodes before edge labels, so a renderer only has to walk
  // the list once.
  items: TSceneItem[];
  // NOTE: Accessible name and description, mirrored into <title>/<desc> by the
  // SVG serializer and into accessibility props by the native renderer.
  accessibleTitle: string;
  accessibleDescription: string;
}

export interface ILayoutDiagramSuccess {
  success: true;
  scene: IDiagramScene;
}

export interface ILayoutDiagramFailure {
  success: false;
  error: string;
}

export type TLayoutDiagramResult =
  | ILayoutDiagramSuccess
  | ILayoutDiagramFailure;
